import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { ModalOverlay, ModalContainer, CloseButton, BackgroundCircle } from "./index.styled";
import downloadIcon from "../../../assets/icons/download.webp";
import uploadIcon from "../../../assets/icons/upload.webp";
import { BACKGROUNDS } from "../../../constants/backgrounds";
import { downloadQuestionWithBackground } from "../../../utils/downloadUtils";

interface BackgroundSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  elementRef?: React.RefObject<HTMLElement | null>;
  id?: string;
  onDownloaded?: () => void;
  cardPosition?: "center" | "top";
  showPersonalTextInput?: boolean;
  personalText?: string;
  onPersonalTextChange?: (text: string) => void;
  mode?: "download" | "select";
  onBackgroundSelected?: (background: string) => void;
  initialBackground?: string;
  title?: string;
  confirmLabel?: string;
}


const BackgroundSelectionModal = ({ 
  isOpen, 
  onClose,
  elementRef,
  id = "card",
  onDownloaded,
  cardPosition = "center",
  showPersonalTextInput = false,
  personalText = "",
  onPersonalTextChange,
  mode = "download",
  onBackgroundSelected,
  initialBackground,
  title = "עיצוב לסטטוס",
  confirmLabel,
}: BackgroundSelectionModalProps) => {
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const [customImage, setCustomImage] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resolveBackground = () =>
    selectedIdx === 7 && customImage
      ? `url(${customImage}) center/cover no-repeat`
      : BACKGROUNDS[selectedIdx];

  useEffect(() => {
    if (!isOpen) return;

    if (!initialBackground) {
      setSelectedIdx(0);
      setCustomImage(null);
      return;
    }

    if (initialBackground.includes("url(")) {
      const url = initialBackground.match(/url\(([^)]+)\)/)?.[1];
      setCustomImage(url?.replace(/['"]/g, "") ?? null);
      setSelectedIdx(7);
      return;
    }

    const idx = BACKGROUNDS.indexOf(initialBackground);
    setSelectedIdx(idx >= 0 ? idx : 0);
    setCustomImage(null);
  }, [isOpen, initialBackground]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (showPersonalTextInput) {
      onPersonalTextChange?.("");
    }
    onClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomImage(reader.result as string);
        setSelectedIdx(7);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfirm = async () => {
    const background = resolveBackground();

    if (mode === "select") {
      onBackgroundSelected?.(background);
      handleClose();
      return;
    }

    if (!elementRef?.current) return;
    
    setIsDownloading(true);
    try {
      await downloadQuestionWithBackground(elementRef.current, background, id, cardPosition);
      onDownloaded?.();
      handleClose();
    } finally {
      setIsDownloading(false);
    }
  };

  const buttonLabel =
    confirmLabel ??
    (mode === "select"
      ? "אישור"
      : isDownloading
        ? "מוריד..."
        : "אישור והורדה");

  const modal = (
    <ModalOverlay onClick={handleClose} $elevated={mode === "select"}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose} aria-label="סגור">
          <X size={17} />
        </CloseButton>
        <h3 className="modal-title">{title}</h3>
        <div className="options-grid">
          {BACKGROUNDS.map((bg, idx) => (
            <BackgroundCircle
              key={idx}
              $background={idx === 7 && customImage ? `url(${customImage}) center/cover no-repeat` : bg}
              $isSelected={selectedIdx === idx}
              $isImage={idx === 7}
              onClick={() => {
                if (idx === 7) {
                  handleUploadClick();
                } else {
                  setSelectedIdx(idx);
                  setCustomImage(null);
                }
              }}
              aria-label={idx === 7 ? "העלאת תמונה אישית" : `אפשרות רקע ${idx + 1}`}
            >
              {idx === 7 && !customImage && <img src={uploadIcon} alt="" className="upload-icon" />}
            </BackgroundCircle>
          ))}
        </div>

        {showPersonalTextInput && (
          <textarea
            className="personal-answer-textarea"
            value={personalText}
            onChange={(e) => onPersonalTextChange?.(e.target.value)}
            placeholder="כאן אפשר להוסיף טקסט תשובה אישי, אבל לא חייב :)"
            aria-label="טקסט תשובה אישי (אופציונלי)"
            rows={4}
          />
        )}

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          style={{ display: "none" }}
        />
        <button 
          className="close-button" 
          onClick={handleConfirm}
          disabled={isDownloading}
        >
          {mode !== "select" && (
            <img 
              src={downloadIcon} 
              alt="" 
              className={`button-icon ${isDownloading ? "loading" : ""}`} 
            />
          )}
          {buttonLabel}
        </button>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modal, document.body);
};

export default BackgroundSelectionModal;
